const TABLE_MARKER_PREFIX = "[TABLE:";
const MAX_SPAN = 100;
const MAX_ROWS = 300;
const MAX_COLS = 50;

export function serializeTableMarker(rows) {
  return `${TABLE_MARKER_PREFIX}${JSON.stringify({ rows })}]`;
}

export function replaceTableMarkers(text, replacement = "[table]") {
  return String(text ?? "")
    .split("\n")
    .map((line) => (parseTableMarker(line) ? replacement : line))
    .join("\n");
}

export function parseTableMarker(line) {
  const trimmed = line.trim();
  if (!trimmed.startsWith(TABLE_MARKER_PREFIX) || !trimmed.endsWith("]")) return null;
  try {
    const data = JSON.parse(trimmed.slice(TABLE_MARKER_PREFIX.length, -1));
    if (!Array.isArray(data?.rows)) return null;
    const rows = data.rows
      .slice(0, MAX_ROWS)
      .filter((row) => Array.isArray(row))
      .map((row) => row.slice(0, MAX_COLS).map(normalizeCell))
      .filter((row) => row.length);
    return rows.length && rows.some((row) => row.some((cell) => cell.t)) ? rows : null;
  } catch {
    return null;
  }
}

function normalizeCell(cell) {
  if (cell === null || typeof cell !== "object") return { t: String(cell ?? "") };
  const normalized = { t: typeof cell.t === "string" ? cell.t : "" };
  if (cell.b) normalized.b = 1;
  if (Number(cell.c) > 1) normalized.c = Math.min(Number(cell.c), MAX_SPAN);
  if (Number(cell.r) > 1) normalized.r = Math.min(Number(cell.r), MAX_SPAN);
  if (cell.a === "c" || cell.a === "r") normalized.a = cell.a;
  return normalized;
}

/**
 * Extract a structured table from the clipboard's text/html flavor (Excel,
 * Google Sheets). Parsing uses an inert DOMParser document; only text and a
 * few style facts (bold, alignment, spans) are kept — markup never survives.
 */
export function extractTableFromHtml(html) {
  if (!html || !html.toLowerCase().includes("<table")) return null;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const table = doc.querySelector("table");
  if (!table) return null;
  const classStyles = parseClassStyles(doc);
  const rows = [...table.querySelectorAll("tr")]
    .slice(0, MAX_ROWS)
    .map((tr) =>
      [...tr.children]
        .filter((el) => /^T[DH]$/.test(el.tagName))
        .slice(0, MAX_COLS)
        .map((td) => extractCell(td, classStyles))
    )
    .filter((row) => row.length);
  return rows.length && rows.some((row) => row.some((cell) => cell.t)) ? rows : null;
}

function parseClassStyles(doc) {
  const map = {};
  for (const styleEl of doc.querySelectorAll("style")) {
    for (const match of styleEl.textContent.matchAll(/\.([\w-]+)\s*\{([^}]*)\}/g)) {
      map[match[1]] = `${map[match[1]] || ""};${match[2].toLowerCase()}`;
    }
  }
  return map;
}

function extractCell(td, classStyles) {
  const classCss = [...td.classList].map((cls) => classStyles[cls] || "").join(";");
  const css = `${classCss};${(td.getAttribute("style") || "").toLowerCase()}`;
  const clone = td.cloneNode(true);
  clone.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
  const text = clone.textContent
    .replace(/\u00a0/g, " ")
    .replace(/\s*\n\s*/g, "\n")
    .trim();
  const cell = { t: text };
  if (/font-weight:\s*(bold|[6-9]00)/.test(css) || (td.tagName === "TH" && text) || td.querySelector("b,strong")) {
    cell.b = 1;
  }
  if (Number(td.getAttribute("colspan")) > 1) cell.c = Math.min(Number(td.getAttribute("colspan")), MAX_SPAN);
  if (Number(td.getAttribute("rowspan")) > 1) cell.r = Math.min(Number(td.getAttribute("rowspan")), MAX_SPAN);
  const align = css.match(/text-align:\s*(center|right)/)?.[1] || (td.getAttribute("align") || "").toLowerCase();
  if (align === "center") cell.a = "c";
  else if (align === "right") cell.a = "r";
  return cell;
}
